import type { TranslationKey } from "src/types/translationKey";

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const IMAGE_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".heic",
  ".heif"
]);

export type FileValidationMode = "image" | "pdf" | "any";

export interface FileValidationError {
  key: TranslationKey;
  variables: Record<string, unknown>;
}

function getFileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

export function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) {
    return true;
  }
  const dot = file.name.lastIndexOf(".");
  if (dot === -1) {
    return false;
  }
  return IMAGE_EXTENSIONS.has(file.name.slice(dot).toLowerCase());
}

export function isPdfFile(file: File): boolean {
  if (file.type === "application/pdf") {
    return true;
  }
  return file.name.toLowerCase().endsWith(".pdf");
}

export function validateFile(
  file: File,
  mode: FileValidationMode
): FileValidationError | null {
  const isImage = isImageFile(file);
  const isPdf = isPdfFile(file);
  const ext = getFileExtension(file.name);

  if (mode === "image" && !isImage) {
    return {
      key: "leapChat.attachment.invalidImageFileType",
      variables: { extension: ext || file.type || "unknown" }
    };
  }
  if (mode === "pdf" && !isPdf) {
    return {
      key: "leapChat.attachment.invalidPdfFileType",
      variables: { extension: ext || file.type || "unknown" }
    };
  }
  if (mode === "any" && !isImage && !isPdf) {
    return {
      key: "leapChat.attachment.invalidFileType",
      variables: { extension: ext || file.type || "unknown" }
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      key: "leapChat.attachment.fileTooLarge",
      variables: { maxSize: `${MAX_FILE_SIZE_MB} MB` }
    };
  }

  return null;
}
